import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Wrench,
  Plus,
  Search,
  MoreHorizontal,
  MapPin,
  Clock,
  User,
  List,
  Calendar as CalendarIcon,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Maintenance = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    property_name: '',
    unit_name: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/maintenance`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    try {
      await axios.post(`${API}/maintenance`, newTicket);
      setIsDialogOpen(false);
      setNewTicket({
        title: '',
        description: '',
        property_name: '',
        unit_name: '',
        priority: 'medium'
      });
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/maintenance/${ticketId}`, { status: newStatus });
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 uppercase text-xs font-bold">Urgent</Badge>;
      case 'medium':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 uppercase text-xs font-bold">Medium</Badge>;
      case 'routine':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 uppercase text-xs font-bold">Routine</Badge>;
      default:
        return <Badge variant="secondary">{priority}</Badge>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-500';
      case 'in_progress':
        return 'bg-amber-500';
      case 'resolved':
        return 'bg-green-500';
      default:
        return 'bg-slate-500';
    }
  };

  const groupedTickets = {
    new: tickets.filter(t => t.status === 'new'),
    in_progress: tickets.filter(t => t.status === 'in_progress'),
    resolved: tickets.filter(t => t.status === 'resolved')
  };

  const activeTickets = tickets.filter(t => t.status !== 'resolved').length;
  const avgResolutionTime = '4.2h';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A56DB]" />
      </div>
    );
  }

  const TicketCard = ({ ticket, isDraggable = false }) => (
    <div 
      className="kanban-card"
      data-testid={`ticket-card-${ticket.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        {getPriorityBadge(ticket.priority)}
        <span className="text-xs text-slate-400">{ticket.ticket_number}</span>
      </div>
      
      <h4 className="font-semibold text-slate-900 mb-2">{ticket.title}</h4>
      <p className="text-sm text-slate-500 mb-3 line-clamp-2">{ticket.description}</p>
      
      <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
        <MapPin className="w-3 h-3" />
        <span>{ticket.property_name} • {ticket.unit_name}</span>
      </div>

      {ticket.image && (
        <img 
          src={ticket.image} 
          alt="Issue" 
          className="w-full h-24 object-cover rounded-lg mb-3"
        />
      )}

      {ticket.assigned_to && (
        <div className="flex items-center gap-2 mb-3">
          {ticket.assigned_avatar ? (
            <img src={ticket.assigned_avatar} alt={ticket.assigned_to} className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
              <User className="w-3 h-3 text-slate-500" />
            </div>
          )}
          <span className="text-xs text-slate-600">{ticket.assigned_to}</span>
          {ticket.progress > 0 && (
            <div className="flex-1 flex items-center gap-2">
              <Progress value={ticket.progress} className="h-1.5 flex-1" />
              <span className="text-xs text-slate-500">{ticket.progress}%</span>
            </div>
          )}
        </div>
      )}

      {ticket.status === 'resolved' && (
        <div className="flex items-center gap-1 text-xs text-green-600">
          <CheckCircle className="w-3 h-3" />
          <span>Verified</span>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        <span className="text-xs text-slate-400">
          {ticket.status === 'resolved' ? 'Yesterday' : '2h ago'}
        </span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="maintenance-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Maintenance Board
          </h1>
          <p className="text-slate-500 mt-1">
            Manage active work orders and property upkeep across your portfolio.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="kanban" data-testid="kanban-view-btn">Kanban</TabsTrigger>
              <TabsTrigger value="list" data-testid="list-view-btn">List View</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="calendar-view-btn">Calendar</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* New Ticket Button */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-[#1A56DB] hover:bg-[#1E40AF]" data-testid="new-ticket-btn">
                New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create Maintenance Ticket</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Issue Title</Label>
                  <Input 
                    id="title"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                    placeholder="e.g., Water Leak in Bathroom"
                    data-testid="ticket-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description"
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    placeholder="Describe the issue in detail..."
                    data-testid="ticket-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="property">Property</Label>
                    <Input 
                      id="property"
                      value={newTicket.property_name}
                      onChange={(e) => setNewTicket({ ...newTicket, property_name: e.target.value })}
                      placeholder="Property name"
                      data-testid="ticket-property-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit">Unit</Label>
                    <Input 
                      id="unit"
                      value={newTicket.unit_name}
                      onChange={(e) => setNewTicket({ ...newTicket, unit_name: e.target.value })}
                      placeholder="Unit number"
                      data-testid="ticket-unit-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select 
                    value={newTicket.priority} 
                    onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                  >
                    <SelectTrigger data-testid="ticket-priority-select">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="routine">Routine</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleCreateTicket} 
                  className="w-full bg-[#1A56DB] hover:bg-[#1E40AF]"
                  data-testid="submit-ticket-btn"
                >
                  Create Ticket
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="kanban-board" data-testid="kanban-board">
        {/* New Requests Column */}
        <div className="kanban-column" data-testid="column-new">
          <div className="kanban-column-header">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getStatusColor('new')}`} />
              <span>New Requests</span>
              <Badge variant="secondary" className="ml-1">{groupedTickets.new.length}</Badge>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
          <div className="kanban-column-body">
            {groupedTickets.new.map(ticket => (
              <TicketCard key={ticket.id} ticket={ticket} />
            ))}
            <Button 
              variant="ghost" 
              className="w-full border-2 border-dashed border-slate-200 text-slate-500 hover:border-slate-300"
              onClick={() => setIsDialogOpen(true)}
              data-testid="add-ticket-inline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New
            </Button>
          </div>
        </div>

        {/* In Progress Column */}
        <div className="kanban-column" data-testid="column-in-progress">
          <div className="kanban-column-header">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getStatusColor('in_progress')}`} />
              <span>In Progress</span>
              <Badge variant="secondary" className="ml-1">{groupedTickets.in_progress.length}</Badge>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
          <div className="kanban-column-body">
            {groupedTickets.in_progress.map(ticket => (
              <TicketCard key={ticket.id} ticket={ticket} />
            ))}
          </div>
        </div>

        {/* Resolved Column */}
        <div className="kanban-column" data-testid="column-resolved">
          <div className="kanban-column-header">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getStatusColor('resolved')}`} />
              <span>Resolved</span>
              <Badge className="ml-1 bg-green-100 text-green-700">{groupedTickets.resolved.length}</Badge>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
          <div className="kanban-column-body">
            {groupedTickets.resolved.map(ticket => (
              <TicketCard key={ticket.id} ticket={ticket} />
            ))}
            <Button variant="link" className="w-full text-slate-500" data-testid="view-archive-btn">
              View Archive
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="fixed bottom-6 right-6 bg-[#1A56DB] text-white rounded-xl px-6 py-4 shadow-lg flex items-center gap-8" data-testid="stats-bar">
        <div className="text-center">
          <p className="text-xs text-blue-200 uppercase tracking-wider">Active Tickets</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {activeTickets.toString().padStart(2, '0')}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-blue-200 uppercase tracking-wider">Avg Resolution</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {avgResolutionTime}
          </p>
        </div>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
          <TrendingUp className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
};

export default Maintenance;
